import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './Blog.css';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const blogPosts = [
    {
      id: 1,
      title: 'How Snap Remedy Built a Self-Healing AI Ops Fabric',
      excerpt: 'A behind-the-scenes look at the observability stack we deployed to give hybrid teams instant insight into every workflow, from edge devices to the cloud.',
      category: 'AI Trends',
      author: 'Sarah Chen',
      date: 'November 14, 2025',
      readTime: '8 min read',
      featured: true,
      image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1400&q=80'
    },
    {
      id: 2,
      title: 'Retail Case Study: 32% Faster Fulfillment with Predictive Service Desks',
      excerpt: 'Discover how a national retailer used Snap Remedy’s automation playbook to triage incidents, shorten ticket queues, and keep associates focused on customers.',
      category: 'Case Studies',
      author: 'Emily Watson',
      date: 'October 28, 2025',
      readTime: '6 min read',
      featured: false,
      image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1400&q=80'
    },
    {
      id: 3,
      title: 'Zero-Trust Automation Checklist for Mid-Market CIOs',
      excerpt: 'Seven controls every IT leader should automate to keep remote workers secure without slowing down collaboration.',
      category: 'Technology',
      author: 'Michael Rodriguez',
      date: 'September 30, 2025',
      readTime: '9 min read',
      featured: false,
      image: 'https://images.unsplash.com/photo-1517430816045-df4b7de11d1d?auto=format&fit=crop&w=1400&q=80'
    },
    {
      id: 4,
      title: 'Five Runbooks That Save Managed Service Teams Hours Each Week',
      excerpt: 'From onboarding automation to compliance-ready backups, we share the templated runbooks that keep Snap Remedy’s NOC humming.',
      category: 'Best Practices',
      author: 'David Kim',
      date: 'September 12, 2025',
      readTime: '5 min read',
      featured: false,
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1400&q=80'
    },
    {
      id: 5,
      title: 'Healthcare Networks and the Race to Close Security Gaps',
      excerpt: 'Lessons from partnering with multi-location clinics that needed HIPAA-aligned infrastructure without rebuilding everything from scratch.',
      category: 'Industry Insights',
      author: 'Priya Nayar',
      date: 'August 25, 2025',
      readTime: '7 min read',
      featured: false,
      image: 'https://images.unsplash.com/photo-1503389152951-9f343605f61e?auto=format&fit=crop&w=1400&q=80'
    },
    {
      id: 6,
      title: 'Why Edge AI Monitoring Matters for Smart Warehouses',
      excerpt: 'We break down the telemetry signals that predict conveyor failures days before downtime hits your SLA.',
      category: 'AI Trends',
      author: 'Luis Ramirez',
      date: 'August 4, 2025',
      readTime: '6 min read',
      featured: false,
      image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1400&q=80'
    },
    {
      id: 7,
      title: 'Building a Composable Data Layer for Finance Teams',
      excerpt: 'Snap Remedy’s reference architecture for unifying risk, payments, and compliance data into a single decision fabric.',
      category: 'Technology',
      author: 'Aisha Bennett',
      date: 'July 22, 2025',
      readTime: '10 min read',
      featured: false,
      image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=1400&q=80'
    },
    {
      id: 8,
      title: 'Modern Incident Reviews: Turning Metrics into Coaching Moments',
      excerpt: 'Our facilitation checklist for blameless post-incident reviews that surface actionable next steps for every stakeholder.',
      category: 'Best Practices',
      author: 'Emily Watson',
      date: 'July 8, 2025',
      readTime: '4 min read',
      featured: false,
      image: 'https://images.unsplash.com/photo-1448932223592-d1fc686e76ea?auto=format&fit=crop&w=1400&q=80'
    }
  ];

  const categories = ['all', 'AI Trends', 'Case Studies', 'Technology', 'Best Practices', 'Industry Insights'];

  const filteredPosts = selectedCategory === 'all'
    ? blogPosts
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPost = blogPosts.find(post => post.featured);

  return (
    <div className="blog">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="hero-container">
          <motion.div
            className="blog-hero-content"
          >
            <div className="hero-label">
              <span className="label-dot"></span>
              Blog & Insights
            </div>
            <h1 className="hero-title">
              Expert Insights on
              <span className="highlight">AI Operations</span>
              {' '}& Technology
            </h1>
            <p className="hero-description">
              Discover practical strategies, real-world case studies, and expert guidance from Snap Remedy's team. Learn how to modernize your IT infrastructure, implement AI solutions, and drive business growth through technology.
            </p>
          </motion.div>
          <div>
            <img src="hero-snap-remedy-blog.svg" className="hero-img" alt="Illustration of people and a rocket taking off" />
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="featured-post-section section-spacing">
          <div className="container">
            <motion.div
              className="featured-post"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="featured-badge">Featured Article</div>
              <div className="featured-content">
                <div className="featured-meta">
                  <span className="post-category">{featuredPost.category}</span>
                  <span className="post-divider">•</span>
                  <span className="post-date">{featuredPost.date}</span>
                  <span className="post-divider">•</span>
                  <span className="post-read-time">{featuredPost.readTime}</span>
                </div>
                <h2 className="featured-title">{featuredPost.title}</h2>
                <p className="featured-excerpt">{featuredPost.excerpt}</p>
                <div className="featured-footer">
                  <span className="post-author">By {featuredPost.author}</span>
                  <a href="#read" className="read-more-link">
                    Read Full Article →
                  </a>
                </div>
              </div>
              <div className="featured-image">
                {featuredPost.image ? (
                  <img
                    src={featuredPost.image}
                    alt={`${featuredPost.title} hero visual`}
                    className="featured-image-media"
                    loading="lazy"
                  />
                ) : (
                  <div className="image-placeholder">
                    <span className="placeholder-text">Featured article image</span>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="category-filter-section">
        <div className="container">
          <div className="category-filter">
            {categories.map((category) => (
              <button
                key={category}
                className={`category-button ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="blog-grid-section section-spacing">
        <div className="container">
          <div className="blog-grid">
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                className="blog-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <div className="blog-card-image">
                  {post.image ? (
                    <img
                      src={post.image}
                      alt={`${post.title} cover art`}
                      className="blog-card-img"
                      loading="lazy"
                    />
                  ) : (
                    <div className="image-placeholder">
                      <span className="placeholder-text">Blog post image</span>
                    </div>
                  )}
                  <span className="blog-category-tag">{post.category}</span>
                </div>

                <div className="blog-card-content">
                  <div className="blog-meta">
                    <span className="post-date">{post.date}</span>
                    <span className="post-divider">•</span>
                    <span className="post-read-time">{post.readTime}</span>
                  </div>

                  <h3 className="blog-card-title">{post.title}</h3>
                  <p className="blog-card-excerpt">{post.excerpt}</p>

                  <div className="blog-card-footer">
                    <span className="post-author">By {post.author}</span>
                    <a href="#read" className="read-more-link">
                      Read →
                    </a>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="newsletter-section section-spacing">
        <div className="container">
          <motion.div
            className="newsletter-box"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="newsletter-title">Join the Snap Remedy Field Notes</h2>
            <p className="newsletter-description">
              Receive a twice-monthly download of practical AI adoption tips, runbooks our engineers actually use, and upcoming events for IT leaders.
            </p>
            <form className="newsletter-form">
              <input
                type="email"
                className="newsletter-input"
                placeholder="you@snapremedy.com"
                required
              />
              <button type="submit" className="newsletter-button">
                Subscribe →
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Blog;

